import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import BottomSheet from '../BottomSheet';
import { measureAnchoredPopover } from './popoverPosition';
import { getTransactionDisplayName } from '../../utils/transactionPresentation';

const MOBILE_BREAKPOINT = 768;

const useMobileLayout = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  return mobile;
};

const StatusConfirmation = ({ anchorRef, item, currentStatus, actionLabel, actionCopy, onCancel, onConfirm }) => {
  const mobile = useMobileLayout();
  const panelRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [saving, setSaving] = useState(false);
  const displayName = getTransactionDisplayName(item);

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onConfirm();
      onCancel();
    } catch (error) {
      setSaving(false);
    }
  };

  const content = (
    <div className="transaction-status-confirmation__content">
      <strong className="transaction-status-confirmation__name">{displayName}</strong>
      <span>Status atual: <strong>{currentStatus}</strong></span>
      <p>{actionCopy}</p>
    </div>
  );

  useLayoutEffect(() => {
    if (mobile) return undefined;
    const updatePosition = () => {
      const next = measureAnchoredPopover({
        anchor: anchorRef.current,
        panel: panelRef.current,
        preferredWidth: 320,
        minimumHeight: 96,
      });
      if (next) setPosition(next);
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, mobile]);

  useEffect(() => {
    if (mobile) return undefined;
    const closeOnOutside = (event) => {
      if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) onCancel();
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [anchorRef, mobile, onCancel]);

  if (mobile) {
    return (
      <BottomSheet
        title="Alterar status"
        ariaLabel={`Alterar status de ${displayName}`}
        onClose={onCancel}
        size="sm"
        className="transaction-status-sheet"
        footer={(
          <>
            <button type="button" className="nf-sheet__primary" onClick={confirm} disabled={saving}>{saving ? 'Atualizando...' : actionLabel}</button>
            <button type="button" className="nf-sheet__secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
          </>
        )}
      >
        {content}
      </BottomSheet>
    );
  }

  return createPortal(
    <section
      ref={panelRef}
      className={`transaction-status-confirmation transaction-status-confirmation--${position?.placement || 'bottom'}`}
      style={position ? { left: position.left, top: position.top, width: position.width, maxHeight: position.maxHeight, visibility: 'visible' } : undefined}
      role="dialog"
      aria-label={`Alterar status de ${displayName}`}
    >
      <h2>Alterar status</h2>
      {content}
      <div className="transaction-status-confirmation__actions">
        <button type="button" className="transaction-status-confirmation__cancel" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button type="button" className="transaction-status-confirmation__confirm" onClick={confirm} disabled={saving}>{saving ? 'Atualizando...' : actionLabel}</button>
      </div>
    </section>,
    document.body
  );
};

StatusConfirmation.propTypes = {
  anchorRef: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  currentStatus: PropTypes.string.isRequired,
  actionLabel: PropTypes.string.isRequired,
  actionCopy: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default StatusConfirmation;

